import React, {
  useReducer,
  useMemo,
  useEffect,
  useContext,
  useRef
} from "react";
import {
  SUBSCRIPTION_INTEGRATIONS,
  SUBSCRIPTION_INTG_CONFIGS,
  SYSTEM_INTEGRATION_CONFIGS
} from "services/graphQL/ciq-queries";

import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { SubscriptionContext } from "context/SubscriptionProvider";
import { OPC_INTEGRATION_SYSTEM } from "constants/integrations";
import {
  IntegrationsTabSharedContext,
  initialState,
  integrationsTabStateReducer
} from "./integrations-tab-context";
import {
  IntegrationType,
  simplifySubscriptionIntegrationConfigs,
  simplifySystemIntegrationConfigs
} from "./utils";
import IntegrationsList from "./integrations-list";
import ProcoreInitialIntegration from "./procore/initial-integration";
import ProcoreIntegrationDetails from "./procore/integration-details";
import AutodeskInitialIntegration from "./autodesk/initial-integration";
import AutodeskIntegrationDetails from "./autodesk/integration-details";
import OpcInitialIntegration from "./opc/initial-integration";
import OpcIntegrationDetails from "./opc/integration-details";

function IntegrationsTab() {
  const { eventLogs = [] } = useContext(SubscriptionContext);
  const [sharedState, dispatch] = useReducer(
    integrationsTabStateReducer,
    initialState
  );

  const {
    data: dataAvailableIntegrations,
    refetch: refetchAvailableIntegrations
  } = useCIQQuery(SUBSCRIPTION_INTEGRATIONS, {});

  const { data: dataSystemIntgConfigs, refetch: refetchSystemIntgConfigs } =
    useCIQQuery(SYSTEM_INTEGRATION_CONFIGS, {});

  const { data: dataIntgConfigs, refetch: refetchIntgConfigs } = useCIQQuery(
    SUBSCRIPTION_INTG_CONFIGS,
    {}
  );

  const providerValue: any = useMemo(
    () => ({
      sharedState,
      dispatch,
      refetchIntegrationConfigs: refetchIntgConfigs
    }),
    [sharedState, refetchIntgConfigs]
  );

  const integrationConfigs = useMemo(() => {
    return simplifySubscriptionIntegrationConfigs(
      dataIntgConfigs?.subscription_integration_configs
    );
  }, [dataIntgConfigs]);

  const systemIntegrationConfigs = useMemo(() => {
    return simplifySystemIntegrationConfigs(
      dataSystemIntgConfigs?.system_integration_configs
    );
  }, [dataSystemIntgConfigs]);

  useEffect(() => {
    if (
      !dataAvailableIntegrations ||
      !integrationConfigs ||
      !systemIntegrationConfigs
    )
      return;
    dispatch({
      type: "SET_SHARED_STATE",
      payload: {
        integrationConfigs,
        systemIntegrationConfigs,
        availableIntegrations: dataAvailableIntegrations.integrations
      }
    });
  }, [dataAvailableIntegrations, integrationConfigs, systemIntegrationConfigs]);

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (eventLogs.length && previousEventLogs.current !== eventLogs) {
      const shouldRefetchIntegrations = eventLogs.some((log) => {
        const dataSource = String(log.data_source || "").toLowerCase();
        return (
          dataSource.includes("subscription_integration_config") &&
          ["SUBSCRIPTION_INTEGRATION_CREATE"].includes(log.change_type)
        );
      });

      if (shouldRefetchIntegrations) {
        refetchAvailableIntegrations?.();
        refetchSystemIntgConfigs?.();
        refetchIntgConfigs?.();
      }
    }
    previousEventLogs.current = eventLogs;
  }, [
    eventLogs,
    refetchAvailableIntegrations,
    refetchSystemIntgConfigs,
    refetchIntgConfigs
  ]);

  return (
    <IntegrationsTabSharedContext.Provider value={providerValue}>
      <div className="bg-white h-[calc(100vh-100px)]">
        {sharedState.activeView === "list" && <IntegrationsList />}
        {sharedState.system === IntegrationType[IntegrationType.PROCORE] &&
          sharedState.activeView === "configure-initial" && (
            <ProcoreInitialIntegration />
          )}
        {sharedState.system === IntegrationType[IntegrationType.AUTODESK] &&
          sharedState.activeView === "configure-initial" && (
            <AutodeskInitialIntegration />
          )}
        {sharedState.system === IntegrationType[IntegrationType.PROCORE] &&
          sharedState.activeView === "details" && <ProcoreIntegrationDetails />}

        {sharedState.system === IntegrationType[IntegrationType.AUTODESK] &&
          sharedState.activeView === "details" && (
            <AutodeskIntegrationDetails />
          )}
        {sharedState.system === OPC_INTEGRATION_SYSTEM &&
          sharedState.activeView === "configure-initial" && (
            <OpcInitialIntegration />
          )}
        {sharedState.system === OPC_INTEGRATION_SYSTEM &&
          sharedState.activeView === "details" && <OpcIntegrationDetails />}
      </div>
    </IntegrationsTabSharedContext.Provider>
  );
}
export default IntegrationsTab;
